import { createTheme } from '@mui/material/styles';

// Colores corporativos de Interbarge (replicando Allocation)
export const colors = {
  brand: {
    red: '#b32317',      // Rojo Interbarge (principal)
    blue: '#002d62',     // Azul Interbarge (secundario)
    gray: '#616161',     // Gris corporativo
  },
  primary: {
    main: '#b32317',    // Rojo Interbarge
    light: '#d94d42',
    dark: '#8a1a11',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#002d62',    // Azul marino Interbarge
    light: '#1a4d8f',
    dark: '#001d42',
    contrastText: '#ffffff',
  },
  background: {
    default: '#fafafa',
    paper: '#ffffff',
    sidebar: '#002d62',  // Azul corporativo para sidebar
    header: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.60)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    white: '#ffffff',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#2e7d32',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#e65100',
  },
  error: {
    main: '#f44336',
    light: '#ef5350',
    dark: '#d32f2f',
  },
  info: {
    main: '#2196f3',
    light: '#42a5f5',
    dark: '#1565c0',
  },
  neutral: {
    white: '#ffffff',
    gray50: '#fafafa',
    gray100: '#f5f5f5',
    gray200: '#eeeeee',
    gray300: '#e0e0e0',
    gray400: '#bdbdbd',
    gray500: '#9e9e9e',
    gray600: '#757575',
    gray700: '#616161',
    gray800: '#424242',
    gray900: '#212121',
    black: '#000000',
  },
  // Colores para evaluación de proveedores
  evaluation: {
    strategic: '#2e7d32',      // Verde oscuro
    preferred: '#4caf50',       // Verde
    developing: '#ff9800',      // Naranja
    conditional: '#ff5722',     // Naranja rojizo
    disabled: '#d32f2f',        // Rojo
  },
  // Colores para pipeline
  pipeline: {
    lead: '#9e9e9e',           // Gris
    qualified: '#2196f3',       // Azul
    proposal: '#ff9800',        // Naranja
    negotiation: '#ffc107',     // Amarillo
    approved: '#4caf50',        // Verde
    rejected: '#f44336',        // Rojo
  },
} as const;

export const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
  },
  typography: {
    fontFamily: [
      'Noto Sans',
      'Calibri',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body1: {
      fontFamily: 'Calibri, sans-serif',
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: 'Calibri, sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      fontFamily: 'Noto Sans, sans-serif',
      fontWeight: 600,
      textTransform: 'none', // Evita MAYÚSCULAS en botones
    },
    caption: {
      fontFamily: 'Calibri, sans-serif',
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e0e0e0',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f7fa',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: '1px solid #e0e0e0',
        },
      },
    },
  },
});
